import React, { useMemo, useRef } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import LocationForm from "../../WorkOrder/Forms/LocationForm";
import { useSelector, useDispatch } from "react-redux";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { useMutation } from "@tanstack/react-query";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { showToast } from "../../../store/toast/toastSlice";
import { isEqual } from "lodash";
import {
  buildValidationMessage,
  focusFirstInvalidField,
  normalizeValidationResult,
} from "../../../utils/formErrors";
import { useResetValidationOnClose } from "../../../hooks/useResetValidationOnClose";

const VALIDATED_FIELDS = ["addressLine1", "addressId"];

const COMPARED_FIELDS = [
  "type",
  "addressId",
  "displayName",
  "locationType",
  "country",
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "zipCode",
  "latitude",
  "longitude",
  "saveForFuture",
  "isRemote",
  "timeZone",
];

const hasValue = (value) => value !== "" && value !== null && value !== undefined;

const hasSaveableLocation = (values) => {
  if (values?.type === "REMOTE") return true;
  if (values?.type === "SAVED_LOCATION") return hasValue(values?.addressId);

  return (
    hasValue(values?.addressLine1) && hasValue(values?.latitude) && hasValue(values?.longitude)
  );
};

const normalizeLocation = (values) =>
  COMPARED_FIELDS.reduce((acc, field) => {
    const value = values?.[field];
    acc[field] = hasValue(value) ? value : "";

    return acc;
  }, {});

const selectSavedLocation = (state) => {
  const workOrder = state.workOrder.workOrderDetails;

  return {
    type: "EXACT_LOCATION",
    addressId: workOrder?.address?.id,
    displayName: workOrder?.address?.displayName,
    locationType: workOrder?.address?.locationType,
    country: workOrder?.address?.country,
    addressLine1: workOrder?.address?.addressLine1,
    addressLine2: workOrder?.address?.addressLine2,
    city: workOrder?.address?.city,
    state: workOrder?.address?.state,
    zipCode: workOrder?.address?.zipCode,
    longitude: workOrder?.address?.longitude,
    latitude: workOrder?.address?.latitude,
    saveForFuture: workOrder?.saveForFuture,
    isRemote: workOrder?.isRemote,
    timeZone: workOrder?.timeZone,
  };
};

const EditLocationModal = ({ open, close }) => {
  const dispatch = useDispatch();
  const locationRef = useRef();

  const id = useSelector((state) => state.workOrder.workOrderDetails.id);
  const { updateWorkOrder } = useUpdateWorkOrder();

  const { isPending: isUpdatingLoading, mutateAsync: updateWorkOrderApiMutation } = useMutation({
    mutationFn: updateWorkOrderApi,
  });

  const location = useSelector((state) => {
    return state.workOrder.scheduling.location;
  });
  const timeZone = useSelector((state) => {
    return state.workOrder.scheduling.schedule.timeZone;
  });
  const savedLocation = useSelector(selectSavedLocation, isEqual);

  const hasChanges = useMemo(
    () => !isEqual(normalizeLocation({ ...location, timeZone }), normalizeLocation(savedLocation)),
    [location, timeZone, savedLocation],
  );
  const canSave = hasSaveableLocation(location);
  useResetValidationOnClose(VALIDATED_FIELDS);

  const handleSubmit = async () => {
    const { valid, errors } = normalizeValidationResult(locationRef.current?.isValid?.());

    if (!valid) {
      const message = buildValidationMessage(errors);
      if (message) {
        dispatch(showToast({ message, type: "error" }));
      }
      focusFirstInvalidField();
      return;
    }

    try {
      await updateWorkOrderApiMutation({ id, ...location, timeZone });

      dispatch(
        showToast({
          message: "Location updated successfully!",
          type: "success",
        }),
      );
      updateWorkOrder();
      close();
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          message: "Failed to update location.",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal open={open} close={close}>
      <div className="py-7 px-12 xl:w-[1400px] lg:w-[1000px] sm:w-[800px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Location</h6>
        <div className="mb-2 mt-3">
          <LocationForm ref={locationRef} isEditMode={true} />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || !canSave || isUpdatingLoading}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditLocationModal;
